import { useEffect, useState } from "react";
import { ArrowLeft, ExternalLink, GitBranch, RefreshCw } from "lucide-react";
import type { GiteaPipelinePage, GiteaRepository } from "@lumen/shared";
import { api } from "../../api";
import { t } from "../../i18n";
import { SettingsPage } from "../settings/ProfilePage";

const pageSize = 30;

function statusClass(status: string) {
  if (status === "success")
    return "bg-emerald-50 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200";
  if (
    ["failure", "timed_out", "action_required", "startup_failure"].includes(
      status,
    )
  )
    return "bg-red-50 text-red-800 dark:bg-red-950/50 dark:text-red-200";
  if (
    ["in_progress", "running", "queued", "waiting", "pending"].includes(status)
  )
    return "bg-amber-50 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200";
  return "bg-stone-100 text-stone-700 dark:bg-white/10 dark:text-stone-200";
}

export function GiteaPage({
  integrationId,
  name,
  organizationId,
  onBack,
}: {
  integrationId: string;
  name: string;
  organizationId?: string;
  onBack: () => void;
}) {
  const [repositories, setRepositories] = useState<GiteaRepository[]>([]);
  const [repository, setRepository] = useState("");
  const [repositoryPage, setRepositoryPage] = useState(1);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<GiteaPipelinePage | null>(null);
  const [repositoriesLoading, setRepositoriesLoading] = useState(true);
  const [runsLoading, setRunsLoading] = useState(false);
  const [repositoryError, setRepositoryError] = useState("");
  const [runError, setRunError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const base = `/integrations/${encodeURIComponent(integrationId)}/gitea`;
  const context = organizationId
    ? `&organizationId=${encodeURIComponent(organizationId)}`
    : "";

  useEffect(() => {
    const controller = new AbortController();
    setRepositoriesLoading(true);
    setRepositoryError("");
    void api<GiteaRepository[]>(`${base}?page=${repositoryPage}${context}`, {
      signal: controller.signal,
    })
      .then((items) => {
        if (controller.signal.aborted) return;
        setRepositories(items);
        setRepository(
          (current) =>
            current ||
            items.find((item) => item.actionsEnabled)?.fullName ||
            items[0]?.fullName ||
            "",
        );
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted)
          setRepositoryError(
            error instanceof Error
              ? error.message
              : t("Could not load repositories"),
          );
      })
      .finally(() => {
        if (!controller.signal.aborted) setRepositoriesLoading(false);
      });
    return () => controller.abort();
  }, [base, context, repositoryPage, refresh]);

  useEffect(() => {
    if (!repository) return;
    const controller = new AbortController();
    const load = async (background = false) => {
      if (background && document.hidden) return;
      if (!background) {
        setRunsLoading(true);
        setData(null);
      }
      try {
        const result = await api<GiteaPipelinePage>(
          `${base}?repository=${encodeURIComponent(repository)}&page=${page}${context}`,
          { signal: controller.signal },
        );
        if (!controller.signal.aborted) {
          setData(result);
          setRunError("");
        }
      } catch (error) {
        if (!controller.signal.aborted)
          setRunError(
            error instanceof Error
              ? error.message
              : t("Could not load pipelines"),
          );
      } finally {
        if (!controller.signal.aborted) setRunsLoading(false);
      }
    };
    setRunError("");
    // Keep active runs current without overlapping slow requests.
    let timer: ReturnType<typeof setTimeout>;
    const poll = () => {
      timer = setTimeout(() => {
        void load(true).finally(() => {
          if (!controller.signal.aborted) poll();
        });
      }, 30000);
    };
    void load().finally(() => {
      if (!controller.signal.aborted) poll();
    });
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [base, context, repository, page, refresh]);

  return (
    <SettingsPage
      title={name}
      description={t("Gitea Actions pipelines and their latest status.")}
      action={
        <button
          className="button"
          disabled={repositoriesLoading || runsLoading}
          onClick={() => setRefresh((value) => value + 1)}
        >
          <RefreshCw size={16} aria-hidden="true" /> {t("Refresh")}
        </button>
      }
    >
      <button className="button mb-5" onClick={onBack}>
        <ArrowLeft size={16} aria-hidden="true" /> {t("Back to connections")}
      </button>
      {repositoryError && (
        <p role="alert" className="mb-4 text-sm text-red-600 dark:text-red-300">
          {repositoryError}
        </p>
      )}
      <section
        className="panel mb-5 p-4 sm:p-5"
        aria-busy={repositoriesLoading}
      >
        <label
          className="mb-2 block text-sm font-semibold"
          htmlFor="gitea-repository"
        >
          {t("Repository")}
        </label>
        <select
          id="gitea-repository"
          className="field w-full"
          value={repository}
          disabled={repositoriesLoading || !repositories.length}
          onChange={(event) => {
            setRepository(event.target.value);
            setPage(1);
            setData(null);
          }}
        >
          {!repositories.length && (
            <option value="">
              {repositoriesLoading
                ? t("Loading repositories…")
                : t("No repositories available")}
            </option>
          )}
          {repository &&
            !repositories.some((item) => item.fullName === repository) && (
              <option value={repository}>{repository}</option>
            )}
          {repositories.map((item) => (
            <option key={item.id} value={item.fullName}>
              {item.fullName}
              {!item.actionsEnabled ? ` · ${t("Actions disabled")}` : ""}
            </option>
          ))}
        </select>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-stone-600 dark:text-stone-300">
            {t("Repository page {{page}}", { page: repositoryPage })}
          </p>
          <div className="flex gap-2">
            <button
              className="button"
              disabled={repositoriesLoading || repositoryPage === 1}
              onClick={() => setRepositoryPage((value) => value - 1)}
            >
              {t("Previous repositories")}
            </button>
            <button
              className="button"
              disabled={repositoriesLoading || repositories.length < pageSize}
              onClick={() => setRepositoryPage((value) => value + 1)}
            >
              {t("More repositories")}
            </button>
          </div>
        </div>
      </section>
      <section className="panel p-4 sm:p-5" aria-busy={runsLoading}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-bold">{t("Pipelines")}</h3>
          <p className="text-sm text-stone-600 dark:text-stone-300">
            {t("Refreshes every 30 seconds")}
          </p>
        </div>
        {runError && (
          <p
            role="alert"
            className="mb-4 text-sm text-red-600 dark:text-red-300"
          >
            {runError}
          </p>
        )}
        {runsLoading ? (
          <p role="status" className="py-8 text-center text-sm">
            {t("Loading pipelines…")}
          </p>
        ) : !repository ? (
          <p className="py-8 text-center text-sm text-stone-600 dark:text-stone-300">
            {t("Select a repository to see its pipelines.")}
          </p>
        ) : data?.runs.length === 0 ? (
          <p className="py-8 text-center text-sm text-stone-600 dark:text-stone-300">
            {t(
              "No pipeline runs found. Enable Gitea Actions and add a workflow to this repository.",
            )}
          </p>
        ) : (
          <div className="space-y-3">
            {data?.runs.map((run) => {
              const status = run.conclusion || run.status;
              return (
                <article
                  key={run.id}
                  className="rounded-xl border border-stone-200 p-4 dark:border-white/10"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h4 className="font-semibold break-words">{run.title}</h4>
                      <p className="mt-1 text-sm break-words text-stone-600 dark:text-stone-300">
                        #{run.number}
                        {run.workflow ? ` · ${run.workflow}` : ""}
                        {run.event ? ` · ${run.event}` : ""}
                      </p>
                    </div>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${statusClass(status)}`}
                    >
                      {status.replaceAll("_", " ")}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-stone-600 dark:text-stone-300">
                    {run.branch && (
                      <span className="inline-flex min-w-0 items-center gap-1 break-all">
                        <GitBranch
                          size={14}
                          className="shrink-0"
                          aria-hidden="true"
                        />
                        {run.branch}
                      </span>
                    )}
                    {run.commit && <code>{run.commit.slice(0, 8)}</code>}
                    {run.updatedAt &&
                      !Number.isNaN(Date.parse(run.updatedAt)) && (
                        <time dateTime={run.updatedAt}>
                          {new Date(run.updatedAt).toLocaleString()}
                        </time>
                      )}
                    {run.url && (
                      <a
                        className="button sm:ml-auto"
                        href={run.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("View in Gitea")}
                        <ExternalLink size={14} aria-hidden="true" />
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
        {data && data.total > pageSize && (
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm">
              {t("Page {{page}} · {{total}} runs", { page, total: data.total })}
            </p>
            <div className="flex gap-2">
              <button
                className="button"
                disabled={runsLoading || page === 1}
                onClick={() => setPage((value) => value - 1)}
              >
                {t("Previous")}
              </button>
              <button
                className="button"
                disabled={runsLoading || page * pageSize >= data.total}
                onClick={() => setPage((value) => value + 1)}
              >
                {t("Next")}
              </button>
            </div>
          </div>
        )}
      </section>
    </SettingsPage>
  );
}
